import { create } from 'zustand'
import { createCartItem, findCartProduct, mergeCartItem } from '@/lib/cart'
import type { FixedDeliveryLocation } from '@/lib/delivery'
import type { Locale } from '@/lib/i18n'
import type { KioskRemoteConfig } from '@/lib/kiosk-config'
import { clearMenuCache } from '@/lib/menu'
import type { CartItem, CompletedOrder, DeliveryLocation, KioskConfig, KioskStep, MenuPage, MenuType, PaymentMethod, Product, SelectedAlternative, TimeSlot } from '@/lib/types'

interface KioskState {
  // Config
  config: KioskConfig | null
  remoteConfig: KioskRemoteConfig | null
  parseToken: string | null
  tillName: string
  adminOpen: boolean
  refreshing: boolean

  // Navigation
  step: KioskStep
  menuType: MenuType | null
  locale: Locale

  // Menu
  pages: MenuPage[]
  restaurantName: string
  paymentMethods: PaymentMethod[]
  deliveryLocations: DeliveryLocation[]
  fixedDeliveryLocation: FixedDeliveryLocation | null
  timeSlots: TimeSlot[]
  showsTip: boolean
  showsStripeSwish: boolean
  /** Native Till.showsMenuType — controls which menu types the kiosk offers.
   *  'All' shows the EatIn/TakeAway choice screen; 'EatIn' or 'TakeAway'
   *  alone auto-advances past the choice. */
  showsMenuType: 'All' | 'EatIn' | 'TakeAway'
  queueTime: string | null
  posTerminalId: string | null
  /** Which provider the selected posTerminalId belongs to (e.g. 'Stripe', 'NetsConnectCloud') */
  posTerminalProvider: string | null
  /** Full list of this till's posTerminals as reported by LocoPOS — used for diagnostics */
  posTerminals: Array<{
    id: string
    name: string
    isOnline: boolean
    isActive: boolean
    paymentProvider: string
  }>
  receiptPrinterId: string | null
  sendOrderToKitchen: string | null
  /** Last payment-intent response from LocoPOS — used in admin modal diagnostics */
  lastPaymentDebug: string | null

  // Browsing
  selectedCategory: string | null
  selectedProduct: Product | null

  activePage: number
  currentCategoryLabel: string | null // shown in landscape header when inside a category
  backAction: (() => void) | null // what landscape header's "Tillbaka" should do
  /** Vad som händer EFTER att en produkt lagts till i kundvagnen (post-add-flow).
   *  Skiljs medvetet från backAction så vi kan ha olika beteenden — t.ex. när
   *  skipCategoryGrid är på och user är i HELA MENYN ska "tillbaka"-knappen
   *  gå till menuTypeSelect, men add-to-cart ska bara stänga modalen och
   *  låta user fortsätta beställa. ProductGrid sätter denna baserat på
   *  current viewingPage. Om null faller ProductModal tillbaka på backAction. */
  afterProductAddedAction: (() => void) | null

  // Cart
  cart: CartItem[]
  editingCartItem: CartItem | null
  tipPercent: number
  selectedDeliveryLoc: string
  selectedTimeSlot: string | null
  orderMessage: string

  // Payment
  payMethod: 'card' | 'swish'
  swishQr: string | null
  paymentOriginStep: 'cart' | 'checkout' | null
  processing: boolean
  error: string | null

  // Order result
  completedOrder: CompletedOrder | null
  receiptHtml: string

  // Loading
  loading: boolean
  menuLoadId: number

  // Actions
  setConfig: (config: KioskConfig) => void
  setRemoteConfig: (config: KioskRemoteConfig | null) => void
  setTillName: (name: string) => void
  setAdminOpen: (open: boolean) => void
  setParseToken: (token: string | null) => void
  setStep: (step: KioskStep) => void
  setMenuType: (type: MenuType) => void
  setLocale: (locale: Locale) => void
  setPages: (pages: MenuPage[]) => void
  setRestaurantName: (name: string) => void
  setPaymentMethods: (methods: PaymentMethod[]) => void
  setDeliveryLocations: (locations: DeliveryLocation[]) => void
  setFixedDeliveryLocation: (location: FixedDeliveryLocation | null) => void
  setTimeSlots: (slots: TimeSlot[]) => void
  setShowsTip: (show: boolean) => void
  setShowsStripeSwish: (show: boolean) => void
  setShowsMenuType: (value: 'All' | 'EatIn' | 'TakeAway') => void
  setPosTerminalId: (id: string | null) => void
  setPosTerminalProvider: (provider: string | null) => void
  setPosTerminals: (
    terminals: Array<{
      id: string
      name: string
      isOnline: boolean
      isActive: boolean
      paymentProvider: string
    }>,
  ) => void
  setReceiptPrinterId: (id: string | null) => void
  setSendOrderToKitchen: (v: string | null) => void
  setLastPaymentDebug: (v: string | null) => void
  setQueueTime: (time: string | null) => void
  setSelectedCategory: (cat: string | null) => void
  setSelectedProduct: (product: Product | null) => void
  setActivePage: (page: number) => void
  setCurrentCategoryLabel: (label: string | null) => void
  setBackAction: (action: (() => void) | null) => void
  setAfterProductAddedAction: (action: (() => void) | null) => void
  setTipPercent: (pct: number) => void
  setSelectedDeliveryLoc: (loc: string) => void
  setSelectedTimeSlot: (slot: string | null) => void
  setOrderMessage: (msg: string) => void
  setPayMethod: (method: 'card' | 'swish') => void
  setSwishQr: (qr: string | null) => void
  setPaymentOriginStep: (step: 'cart' | 'checkout') => void
  returnFromPayment: (error?: string | null) => void
  setProcessing: (p: boolean) => void
  setError: (err: string | null) => void
  setCompletedOrder: (order: CompletedOrder | null) => void
  setReceiptHtml: (html: string) => void
  setLoading: (l: boolean) => void

  /** Lägger en produkt med dess native LocoPOS-alternativ i kundvagnen. */
  addToCart: (product: Product, alternatives: SelectedAlternative[], message?: string) => void
  updateCartCount: (idx: number, delta: number) => void
  editCartItem: (item: CartItem) => void
  saveCartItem: (alternatives: SelectedAlternative[], message?: string) => void
  clearCart: () => void

  totalAmount: () => number
  totalItems: () => number

  resetToAttractor: () => void
  resetForNewOrder: () => void
}

export const useKioskStore = create<KioskState>((set, get) => ({
  // Initial state
  config: null,
  remoteConfig: null,
  parseToken: null,
  tillName: '',
  adminOpen: false,
  refreshing: false,
  step: 'menuTypeSelect',
  menuType: null,
  locale: 'sv',
  pages: [],
  restaurantName: '',
  paymentMethods: [],
  deliveryLocations: [],
  fixedDeliveryLocation: null,
  timeSlots: [],
  showsTip: false,
  showsStripeSwish: false,
  showsMenuType: 'All',
  posTerminalId: null,
  posTerminalProvider: null,
  posTerminals: [],
  receiptPrinterId: null,
  sendOrderToKitchen: null,
  lastPaymentDebug: null,
  queueTime: null,
  selectedCategory: null,
  selectedProduct: null,
  activePage: 0,
  currentCategoryLabel: null,
  backAction: null,
  afterProductAddedAction: null,
  cart: [],
  editingCartItem: null,
  tipPercent: 0,
  selectedDeliveryLoc: '',
  selectedTimeSlot: null,
  orderMessage: '',
  payMethod: 'card',
  swishQr: null,
  paymentOriginStep: null,
  processing: false,
  error: null,
  completedOrder: null,
  receiptHtml: '',
  loading: false,
  menuLoadId: 0,

  // Setters
  setConfig: (config) => {
    clearMenuCache()
    set({ config })
  },
  setRemoteConfig: (remoteConfig) => set({ remoteConfig }),
  setTillName: (tillName) => set({ tillName }),
  setAdminOpen: (adminOpen) => set({ adminOpen }),
  setParseToken: (parseToken) => {
    if (get().parseToken !== parseToken) clearMenuCache()
    set({ parseToken })
  },
  setStep: (step) => set({ step, error: null }),
  setMenuType: (menuType) => set({ menuType }),
  setLocale: (locale) => set({ locale }),
  setPages: (pages) => set({ pages }),
  setRestaurantName: (restaurantName) => set({ restaurantName }),
  setPaymentMethods: (paymentMethods) => set({ paymentMethods }),
  setDeliveryLocations: (deliveryLocations) => set({ deliveryLocations }),
  setFixedDeliveryLocation: (fixedDeliveryLocation) => set({ fixedDeliveryLocation }),
  setTimeSlots: (timeSlots) => set({ timeSlots }),
  setShowsTip: (showsTip) => set({ showsTip }),
  setShowsStripeSwish: (showsStripeSwish) => set({ showsStripeSwish }),
  setShowsMenuType: (showsMenuType) => set({ showsMenuType }),
  setPosTerminalId: (posTerminalId) => set({ posTerminalId }),
  setPosTerminalProvider: (posTerminalProvider) => set({ posTerminalProvider }),
  setPosTerminals: (posTerminals) => set({ posTerminals }),
  setReceiptPrinterId: (receiptPrinterId) => set({ receiptPrinterId }),
  setSendOrderToKitchen: (sendOrderToKitchen) => set({ sendOrderToKitchen }),
  setLastPaymentDebug: (lastPaymentDebug) => set({ lastPaymentDebug }),
  setQueueTime: (queueTime) => set({ queueTime }),
  setSelectedCategory: (selectedCategory) => set({ selectedCategory }),
  setSelectedProduct: (selectedProduct) => set({ selectedProduct, editingCartItem: null }),
  setActivePage: (activePage) => set({ activePage }),
  setCurrentCategoryLabel: (currentCategoryLabel) => set({ currentCategoryLabel }),
  setBackAction: (backAction) => set({ backAction }),
  setAfterProductAddedAction: (action) => set({ afterProductAddedAction: action }),
  setTipPercent: (tipPercent) => set({ tipPercent }),
  setSelectedDeliveryLoc: (selectedDeliveryLoc) => set({ selectedDeliveryLoc }),
  setSelectedTimeSlot: (selectedTimeSlot) => set({ selectedTimeSlot }),
  setOrderMessage: (orderMessage) => set({ orderMessage }),
  setPayMethod: (payMethod) => set({ payMethod }),
  setSwishQr: (swishQr) => set({ swishQr }),
  setPaymentOriginStep: (paymentOriginStep) => set({ paymentOriginStep }),
  returnFromPayment: (error = null) => set((state) => ({ step: state.paymentOriginStep ?? 'cart', error })),
  setProcessing: (processing) => set({ processing }),
  setError: (error) => set({ error }),
  setCompletedOrder: (completedOrder) => set({ completedOrder }),
  setReceiptHtml: (receiptHtml) => set({ receiptHtml }),
  setLoading: (loading) => set({ loading }),

  // Cart actions
  addToCart: (product, alternatives, message) => {
    const item = createCartItem(product, alternatives, message)
    if (!item) return
    set((state) => ({ cart: mergeCartItem(state.cart, item), selectedProduct: null }))
  },

  updateCartCount: (idx, delta) => {
    set((state) => ({
      cart: state.cart.map((item, i) => (i === idx ? { ...item, count: Math.max(0, item.count + delta) } : item)).filter((i) => i.count > 0),
    }))
  },

  editCartItem: (item) => {
    const state = get()
    const product = findCartProduct(state.pages, item)
    if (!product || !state.cart.includes(item) || state.processing) return
    set({ selectedProduct: product, editingCartItem: item })
  },

  saveCartItem: (alternatives, message) => {
    const { cart, selectedProduct, editingCartItem } = get()
    if (!selectedProduct || !editingCartItem || !cart.includes(editingCartItem)) return
    const updated = createCartItem(selectedProduct, alternatives, message, editingCartItem.count)
    if (!updated) return
    set({
      cart: cart.map((item) => (item === editingCartItem ? updated : item)).reduce<CartItem[]>((result, item) => mergeCartItem(result, item), []),
      editingCartItem: null,
      selectedProduct: null,
    })
  },

  clearCart: () => set({ cart: [], tipPercent: 0, orderMessage: '', editingCartItem: null, selectedProduct: null }),

  totalAmount: () => get().cart.reduce((s, i) => s + i.price * i.count, 0),
  totalItems: () => get().cart.reduce((s, i) => s + i.count, 0),

  // Reset entire kiosk back to attractor (between customers)
  resetToAttractor: () => {
    clearMenuCache()
    set({
      step: 'menuTypeSelect',
      menuType: null,
      pages: [],
      selectedCategory: null,
      selectedProduct: null,
      activePage: 0,
      currentCategoryLabel: null,
      backAction: null,
      afterProductAddedAction: null,
      cart: [],
      editingCartItem: null,
      tipPercent: 0,
      selectedDeliveryLoc: '',
      selectedTimeSlot: null,
      orderMessage: '',
      payMethod: 'card',
      swishQr: null,
      paymentOriginStep: null,
      processing: false,
      error: null,
      completedOrder: null,
      receiptHtml: '',
      loading: false,
      menuLoadId: get().menuLoadId + 1,
    })
  },

  // Reset for a new order (after receipt, keep menu loaded)
  resetForNewOrder: () =>
    set({
      step: 'browsing',
      selectedCategory: null,
      selectedProduct: null,
      currentCategoryLabel: null,
      backAction: null,
      afterProductAddedAction: null,
      cart: [],
      editingCartItem: null,
      tipPercent: 0,
      orderMessage: '',
      payMethod: 'card',
      swishQr: null,
      paymentOriginStep: null,
      processing: false,
      error: null,
      completedOrder: null,
      receiptHtml: '',
    }),
}))
